/**
 * Список разговоров раскрытой строки «Голоса клиента»: формулировка (если есть),
 * пояснение, дата и переход в разговор. Один на раскрытие строки
 * (`VoiceRowDetails`) и на вопросы без ответа темы (`VoiceThemeDetails`).
 */
import { useTranslation } from 'react-i18next';
import { formatDateShort } from '@/lib/utils';
import {
  itemDetail,
  itemText,
  type VoiceRow,
  type VoiceRowItem,
} from './voice-view-model';

interface VoiceItemListProps {
  /** Вид строки решает, как читается пояснение (`itemDetail`). */
  row: Pick<VoiceRow, 'kind'>;
  items: readonly VoiceRowItem[];
  onOpenCall: (recordId: string) => void;
  /**
   * What an empty wording says. By default — «wording hidden» (the redaction
   * removed it); a string — that text; `null` — nothing: the conversation
   * may have had no wording to remove (a decline without a named reason, a
   * theme conversation: a theme has no wording at all).
   */
  emptyWording?: string | null;
}

export function VoiceItemList({
  row,
  items,
  onOpenCall,
  emptyWording,
}: VoiceItemListProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const empty = (item: VoiceRowItem) =>
    emptyWording === undefined ? itemText(item, t) : emptyWording;
  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const detail = itemDetail(row, item, t);
        const placeholder = item.text ? null : empty(item);
        return (
          <li key={`${item.recordId}-${item.text}`} className="text-sm">
            {item.text ? (
              <p className="text-fg">«{item.text}»</p>
            ) : (
              placeholder && <p className="text-fg-subtle">{placeholder}</p>
            )}
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
              {detail && <span>{detail}</span>}
              <span>{formatDateShort(item.createdAt)}</span>
              <button
                type="button"
                className="font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onOpenCall(item.recordId)}
              >
                {t('voicePage.openCall')}
              </button>
            </p>
          </li>
        );
      })}
    </ul>
  );
}
